'use client';

import { buildShopRingCards } from 'components/products/shopRingCards';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { TaxConfigKey } from 'components/shared/Cart/utils';
import { ringProKeyT } from 'content/products/ring-pro/ringProKeyT';
import { getBloodVisionSubscriptionCards } from 'content/products/bloodVisionSubscriptionCards';
import { getBloodVisionUpsellCards } from 'content/products/bloodVisionUpsell';
import { getM1DataForStore } from 'content/products/m1';
import { M1ProductData } from 'content/products/m1/IN';
import { ProductPurchaseOption } from 'content/products/productMap';
import { HomePrice } from 'content/products/uhHome';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import type { UrlObject } from 'url';
import { isMCFCountry } from 'lib/region/countryUtils';
import { Currency } from 'utils/Currency';
import {
  getEffectiveStoreForDiscount,
  ShopifyStore,
} from 'lib/commerce/shopify/shopifyHelper';
import {
  AffiliateProductCard,
  type AffiliateProductCardProps,
} from './AffiliateProductCard';
import { OtherProductsCarousel } from './OtherProductsCarousel';
import styles from './AffiliateProducts.module.css';

type AffiliateDiscount = {
  code?: string;
  type?: string;
  value?: number | string;
};

export type AffiliateLandingData = {
  name?: string | null;
  code?: string | null;
  ring_active?: boolean | null;
  ring_pro_active?: boolean | null;
  ring_diesel_active?: boolean | null;
  ring_rare_active?: boolean | null;
  m1_active?: boolean | null;
  home_active?: boolean | null;
  blood_vision_active?: boolean | null;
  all_products_active?: boolean | null;
  discount_config?: Record<string, Record<string, AffiliateDiscount>> | null;
};

const RING_ORDER = ['pro', 'air', 'diesel', 'rare'] as const;
type RingVariant = (typeof RING_ORDER)[number];

const DISCOUNT_KEY: Record<RingVariant, string> = {
  pro: 'RING_PRO',
  air: 'RING',
  diesel: 'RING_DIESEL',
  rare: 'RING_RARE',
};

// Prototype landing stagger: the top row waits a beat, then runs left to right.
const ENTRANCE_BASE_MS = 350;
const ENTRANCE_STEP_MS = 140;

type ProductEntry = {
  key: string;
  /** The affiliate covers this product: it leads with its discount and code. */
  valid: boolean;
  ringVariant?: RingVariant;
  card: Omit<AffiliateProductCardProps, 'currency' | 'taxLabel'>;
};

function percentageOff(
  discounts: Record<string, AffiliateDiscount> | undefined,
  key: string,
): number | undefined {
  const config = discounts?.[key];
  if (!config || config.type !== 'percentage') return undefined;
  const value = Number(config.value);
  if (!Number.isFinite(value) || value <= 0 || value >= 100) return undefined;
  return value;
}

function discountCode(
  discounts: Record<string, AffiliateDiscount> | undefined,
  key: string,
): string | undefined {
  const code = discounts?.[key]?.code;
  return typeof code === 'string' && code.trim().length > 0 ? code : undefined;
}

// Buy now already carries the codes: the affiliate for attribution, the promo for the discount.
function buyLink(
  href: string,
  affiliateCode?: string | null,
  promoCode?: string,
): UrlObject {
  const url = new URL(href, 'https://www.ultrahuman.com');
  const query: Record<string, string> = {};
  url.searchParams.forEach((value, key) => {
    query[key] = value;
  });
  if (affiliateCode) query.affiliateCode = affiliateCode;
  if (promoCode) query.discount = promoCode;
  return { pathname: url.pathname, query };
}

export const AffiliateProducts = ({
  affiliate,
}: {
  affiliate: AffiliateLandingData;
}) => {
  const t = useTranslations('invite');
  const tRing = useTranslations('ring');
  const tRingPro = useTranslations('ring-pro');
  const tHome = useTranslations('homepage');
  const tCart = useTranslations('cart');
  const cart = useCartStore(
    useShallow((s) => ({ store: s.store, currency: s.currency })),
  );
  const {
    region,
    store: regionStore,
    currency: regionCurrency,
  } = useRegionLocale();
  const resolvedStore = (cart.store ??
    regionStore ??
    ShopifyStore.ROW) as ShopifyStore;
  const currency = (cart.currency ??
    regionCurrency ??
    Currency.usd) as Currency;
  const m1Store = isMCFCountry(region) ? ShopifyStore.EU : resolvedStore;
  const isUS = resolvedStore === ShopifyStore.US;
  // US, AE, ROW and CA quote prices before tax.
  const taxLabel = tCart(
    TaxConfigKey[resolvedStore] ?? 'sharedCart.cartUi.taxIncl',
  );

  // A product is active for this region only when the store's discount config
  // carries a code for it; an explicit `*_active: false` still switches it off.
  // Inactive products still show, under "Other products".
  const validity = useMemo(
    () => ({
      pro: affiliate.ring_pro_active !== false,
      air: affiliate.ring_active !== false,
      diesel: affiliate.ring_diesel_active !== false,
      rare: affiliate.ring_rare_active !== false,
      home: affiliate.home_active !== false,
      m1: affiliate.m1_active !== false,
      bloodVision: affiliate.blood_vision_active !== false,
    }),
    [affiliate],
  );

  // The API keys the config by IN / AE / US / ROW; every other store (EU, GB,
  // AU, …) is rest-of-world, as on the previous invite page.
  const discounts = useMemo(() => {
    const config = affiliate.discount_config;
    const storeKey = getEffectiveStoreForDiscount(resolvedStore);
    return (
      config?.[storeKey] ??
      config?.[resolvedStore] ??
      config?.[ShopifyStore.ROW]
    );
  }, [affiliate.discount_config, resolvedStore]);

  const products = useMemo<ProductEntry[]>(() => {
    const entries: ProductEntry[] = [];
    const affiliateCode = affiliate.code;

    const isActive = (flagValid: boolean, key: string) =>
      flagValid && discountCode(discounts, key) !== undefined;
    const offer = (valid: boolean, key: string) =>
      valid
        ? {
            percent: percentageOff(discounts, key),
            code: discountCode(discounts, key),
          }
        : { percent: undefined, code: undefined };

    const byVariant = new Map(
      buildShopRingCards(
        resolvedStore,
        currency,
        ringProKeyT(tRing, tRingPro),
      ).map((card) => [card.variant, card]),
    );
    RING_ORDER.forEach((variant) => {
      // The US store sells neither Ring AIR nor Ring RARE.
      if (isUS && (variant === 'air' || variant === 'rare')) return;
      const card = byVariant.get(variant);
      const image = card?.colors[0]?.mainImage;
      if (!card || !image) return;
      const valid = isActive(validity[variant], DISCOUNT_KEY[variant]);
      const { percent, code } = offer(valid, DISCOUNT_KEY[variant]);
      entries.push({
        key: variant,
        valid,
        ringVariant: variant,
        card: {
          title: card.title,
          description: card.description,
          imageSrc: image,
          imageAlt: card.title,
          // Prototype nudges: PRO -2.16px, AIR 1.84px.
          imageOffsetY:
            variant === 'pro' ? -2.16 : variant === 'air' ? 1.84 : 0,
          price: card.fromPrice,
          discountPercentage: percent,
          code,
          href: buyLink(card.buyHref, affiliateCode, code),
        },
      });
    });

    const homePrice = HomePrice[resolvedStore] ?? 0;
    if (homePrice > 0) {
      const homeValid = isActive(validity.home, 'HOME');
      const { percent, code } = offer(homeValid, 'HOME');
      entries.push({
        key: 'home',
        valid: homeValid,
        card: {
          title: tHome('homepage.featured.uhHome.title'),
          description: tRing('ring.shop.products.home.description'),
          imageSrc: 'web_v2/shop/home/home-1.png',
          imageAlt: tHome('homepage.featured.uhHome.title'),
          imageFit: 'photo',
          imagePosition: '50% 56%',
          price: homePrice,
          discountPercentage: percent,
          code,
          // The /home marketing page's own CTA drops the query; go straight to its buy page.
          href: buyLink('/home/buy', affiliateCode, code),
        },
      });
    }

    const m1Otp = getM1DataForStore(m1Store, region?.toUpperCase())?.[
      ProductPurchaseOption.oneTimePurchase
    ];
    const m1Plans = m1Otp?.plans
      ? (Object.values(m1Otp.plans).filter(Boolean) as M1ProductData[])
      : [];
    const m1Price =
      (m1Otp?.plans[m1Otp.defaultProduct]?.price as number | undefined) ??
      m1Plans[0]?.price;
    if (typeof m1Price === 'number' && m1Price > 0) {
      const m1Model = region === 'US' ? 'M2' : 'M1';
      const m1Valid = isActive(validity.m1, 'M1');
      const { percent, code } = offer(m1Valid, 'M1');
      entries.push({
        key: 'm1',
        valid: m1Valid,
        card: {
          title: t('products.m1.title', { model: m1Model }),
          description: tRing('ring.shop.products.m1Otp.description'),
          imageSrc:
            region === 'US'
              ? 'web_v2/shop/m2/m2-otp.png'
              : 'web_v2/shop/m1/otp-1.png',
          imageAlt: t('products.m1.title', { model: m1Model }),
          imageFit: 'photo',
          imagePosition: '50% 28%',
          price: m1Price,
          fromPrice: true,
          discountPercentage: percent,
          code,
          href: buyLink('/pricing', affiliateCode, code),
        },
      });
    }

    // Blood Vision collapses to one "From" card, like the prototype. Where the
    // store sells plans (US), the floor is the entry plan, never an add-on test;
    // elsewhere (IN, AE) the one-time tests are the products.
    const bvPlans = getBloodVisionSubscriptionCards(resolvedStore).map(
      (c) => c.fromPrice,
    );
    const bvPrices = (
      bvPlans.length > 0
        ? bvPlans
        : getBloodVisionUpsellCards(resolvedStore).map((c) => c.fallbackPrice)
    ).filter(
      (price): price is number => typeof price === 'number' && price > 0,
    );
    if (bvPrices.length > 0) {
      const bvValid = isActive(validity.bloodVision, 'BLOOD_VISION');
      const { percent, code } = offer(bvValid, 'BLOOD_VISION');
      entries.push({
        key: 'bloodVision',
        valid: bvValid,
        card: {
          title: t('products.bloodVision.title'),
          description: t('products.bloodVision.description'),
          imageSrc: 'web_v2/blood-vision/bv-phone-ss-4x.png',
          imageAlt: t('products.bloodVision.title'),
          imageFit: 'contain',
          price: Math.min(...bvPrices),
          fromPrice: true,
          discountPercentage: percent,
          code,
          href: buyLink('/blood-vision/buy', affiliateCode, code),
        },
      });
    }

    return entries;
  }, [
    affiliate.code,
    discounts,
    validity,
    resolvedStore,
    currency,
    isUS,
    m1Store,
    region,
    t,
    tRing,
    tRingPro,
    tHome,
  ]);

  const featured = products.filter((p) => p.valid);
  const others = products.filter((p) => !p.valid);

  if (products.length === 0) return null;

  return (
    <section className={styles.section} aria-label={t('products.label')}>
      {featured.length > 0 ? (
        <div className={styles.group}>
          <div className={styles.row}>
            {featured.map((product, index) => (
              <AffiliateProductCard
                key={product.key}
                {...product.card}
                introLabel={
                  product.ringVariant
                    ? t(`rings.${product.ringVariant}`)
                    : undefined
                }
                currency={currency}
                taxLabel={taxLabel}
                priority={index === 0}
                entrance
                delayMs={ENTRANCE_BASE_MS + index * ENTRANCE_STEP_MS}
              />
            ))}
          </div>
        </div>
      ) : null}

      {others.length > 0 ? (
        <div className={styles.group}>
          {featured.length > 0 ? (
            <h2 className={styles.title}>{t('otherProducts')}</h2>
          ) : null}
          {/* storeHref={buyLink('/shop', affiliate.code)} once /shop cards honour affiliate codes */}
          <OtherProductsCarousel
            titles={others.map((product) => product.card.title)}
          >
            {others.map((product) => (
              <AffiliateProductCard
                key={product.key}
                {...product.card}
                currency={currency}
                taxLabel={taxLabel}
                priority={featured.length === 0}
                inCarousel
              />
            ))}
          </OtherProductsCarousel>
        </div>
      ) : null}
    </section>
  );
};
